import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { subscribeToMediaQuery } from '../utils/mediaQuery';

export interface Theme {
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  primary: string;
  primaryHover: string;
  accent: string;
  accentStrong: string;
  border: string;
  shadow: string;
  // Status colours for the organiser submission surfaces. Backgrounds are the
  // SOLID equivalents of the old translucent tints blended over this theme's
  // surface, and every fg/bg pair is computed to meet WCAG 4.5:1 in this theme
  // (see PR #18 review finding 7 for the computed ratios).
  statusPendingBg: string;
  statusPendingFg: string;
  statusPendingBorder: string;
  statusApprovedBg: string;
  statusApprovedFg: string;
  statusApprovedBorder: string;
  statusRejectedBg: string;
  statusRejectedFg: string;
  statusRejectedBorder: string;
  statusVoidBg: string;
  statusVoidFg: string;
  statusVoidBorder: string;
  // Danger text rendered directly on the surface (field errors, danger buttons).
  dangerFg: string;
}

export const lightTheme: Theme = {
  background: 'linear-gradient(180deg, #f7fafc 0%, #eef3f7 46%, #f7efe2 100%)',
  surface: '#ffffff',
  text: '#1f2933',
  textSecondary: '#5f6f7f',
  primary: '#256f91',
  primaryHover: '#1d5c78',
  accent: '#e9b339',
  accentStrong: '#ff7b5a',
  border: '#d7e0e8',
  shadow: 'rgba(27, 39, 51, 0.12)',
  statusPendingBg: '#fff5d7',
  statusPendingFg: '#705400',
  statusPendingBorder: '#d9b64a',
  statusApprovedBg: '#e1f3e5',
  statusApprovedFg: '#186c2c',
  statusApprovedBorder: '#9fd0ac',
  statusRejectedBg: '#fbe7e9',
  statusRejectedFg: '#b02a37',
  statusRejectedBorder: '#e8a7ae',
  statusVoidBg: '#e9ebed',
  statusVoidFg: '#4c5966',
  statusVoidBorder: '#b9c2c9',
  dangerFg: '#b02a37'
};

export const darkTheme: Theme = {
  background: 'linear-gradient(180deg, #101820 0%, #142230 48%, #1f2937 100%)',
  surface: '#18232f',
  text: '#f4f7fb',
  textSecondary: '#b8c4d2',
  primary: '#62b6d8',
  primaryHover: '#8bd0ec',
  accent: '#f2be4d',
  accentStrong: '#ff8a65',
  border: '#314456',
  shadow: 'rgba(0, 0, 0, 0.34)',
  statusPendingBg: '#3d3c29',
  statusPendingFg: '#ffd75e',
  statusPendingBorder: '#6b5f2a',
  statusApprovedBg: '#1a3532',
  statusApprovedFg: '#7ee0a3',
  statusApprovedBorder: '#2f5c47',
  statusRejectedBg: '#302532',
  statusRejectedFg: '#ff9ea8',
  statusRejectedBorder: '#5c3a44',
  statusVoidBg: '#2e3a46',
  statusVoidFg: '#b8c4d2',
  statusVoidBorder: '#4a5866',
  dangerFg: '#ff9ea8'
};

// The SAME key and JSON-boolean format the pre-paint script in
// public/index.html reads, so the theme it paints before first render always
// matches the theme React settles on. Keep both in sync.
const DARK_MODE_STORAGE_KEY = 'darkMode';

// The stored explicit choice, or null when nothing has been saved yet
// (storage denied counts as nothing saved: fail safe to the OS preference).
const readStoredPreference = (): boolean | null => {
  try {
    const saved = localStorage.getItem(DARK_MODE_STORAGE_KEY);
    return saved !== null ? JSON.parse(saved) : null;
  } catch (error) {
    return null;
  }
};

const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (error) {
    return false;
  }
};

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const stored = readStoredPreference();
    return stored !== null ? stored : getSystemPrefersDark();
  });
  // Whether the current value came from an explicit user choice (stored in
  // localStorage) rather than the OS default. Only an explicit choice is
  // persisted, and only while nothing is explicit does an OS change (below)
  // keep updating the theme: a stored explicit choice always wins over the
  // OS (see issue #122).
  const hasExplicitChoiceRef = useRef(readStoredPreference() !== null);

  useEffect(() => {
    const dataTheme = isDark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', dataTheme);
    
    // Also set on body as fallback
    document.body.setAttribute('data-theme', dataTheme);
    
    // Apply theme immediately to prevent flash
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.style.setProperty('--background', isDark ? darkTheme.background : lightTheme.background);
      root.style.setProperty('--surface', isDark ? darkTheme.surface : lightTheme.surface);
      root.style.setProperty('--text', isDark ? darkTheme.text : lightTheme.text);
      root.style.setProperty('--text-secondary', isDark ? darkTheme.textSecondary : lightTheme.textSecondary);
      root.style.setProperty('--textSecondary', isDark ? darkTheme.textSecondary : lightTheme.textSecondary);
      root.style.setProperty('--primary', isDark ? darkTheme.primary : lightTheme.primary);
      root.style.setProperty('--primary-hover', isDark ? darkTheme.primaryHover : lightTheme.primaryHover);
      root.style.setProperty('--primaryHover', isDark ? darkTheme.primaryHover : lightTheme.primaryHover);
      root.style.setProperty('--accent', isDark ? darkTheme.accent : lightTheme.accent);
      root.style.setProperty('--accent-strong', isDark ? darkTheme.accentStrong : lightTheme.accentStrong);
      root.style.setProperty('--border', isDark ? darkTheme.border : lightTheme.border);
      root.style.setProperty('--shadow', isDark ? darkTheme.shadow : lightTheme.shadow);
      root.style.setProperty('--status-pending-bg', isDark ? darkTheme.statusPendingBg : lightTheme.statusPendingBg);
      root.style.setProperty('--status-pending-fg', isDark ? darkTheme.statusPendingFg : lightTheme.statusPendingFg);
      root.style.setProperty('--status-pending-border', isDark ? darkTheme.statusPendingBorder : lightTheme.statusPendingBorder);
      root.style.setProperty('--status-approved-bg', isDark ? darkTheme.statusApprovedBg : lightTheme.statusApprovedBg);
      root.style.setProperty('--status-approved-fg', isDark ? darkTheme.statusApprovedFg : lightTheme.statusApprovedFg);
      root.style.setProperty('--status-approved-border', isDark ? darkTheme.statusApprovedBorder : lightTheme.statusApprovedBorder);
      root.style.setProperty('--status-rejected-bg', isDark ? darkTheme.statusRejectedBg : lightTheme.statusRejectedBg);
      root.style.setProperty('--status-rejected-fg', isDark ? darkTheme.statusRejectedFg : lightTheme.statusRejectedFg);
      root.style.setProperty('--status-rejected-border', isDark ? darkTheme.statusRejectedBorder : lightTheme.statusRejectedBorder);
      root.style.setProperty('--status-void-bg', isDark ? darkTheme.statusVoidBg : lightTheme.statusVoidBg);
      root.style.setProperty('--status-void-fg', isDark ? darkTheme.statusVoidFg : lightTheme.statusVoidFg);
      root.style.setProperty('--status-void-border', isDark ? darkTheme.statusVoidBorder : lightTheme.statusVoidBorder);
      root.style.setProperty('--danger-fg', isDark ? darkTheme.dangerFg : lightTheme.dangerFg);
    }
  }, [isDark]);

  // Keeps following the OS theme while open, for as long as the visitor has
  // never made an explicit choice. Once toggleTheme runs, hasExplicitChoiceRef
  // is true and this listener stops changing anything.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    let media: MediaQueryList;
    try {
      media = window.matchMedia('(prefers-color-scheme: dark)');
    } catch (error) {
      return undefined;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      if (hasExplicitChoiceRef.current) return;
      setIsDark(event.matches);
    };

    return subscribeToMediaQuery(media, handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDark((previous) => {
      const next = !previous;
      hasExplicitChoiceRef.current = true;
      try {
        localStorage.setItem(DARK_MODE_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        // Storage denied: the choice still applies for this session via
        // state, it just will not survive a reload.
      }
      return next;
    });
  };

  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
